import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { Button, Col, Collapse, Divider, Input, Row } from 'antd';

import { TScheduleTemplate } from '@components/schedule/common/types';
import {
    faAngleDown,
    faAngleLeft,
    faCancel,
    faEdit,
    faPlus,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const { Panel } = Collapse;
const { TextArea } = Input;

type TProps = {
    isOpen: boolean;
    flipIsOpen: () => void;
    templates: TScheduleTemplate[];
    selectedTemplateId?: number;
    onTemplateSelect: (templateId: number) => void;
    isEditing: boolean;
    isCreating: boolean;
    flipIsEditing: () => void;
    flipIsCreating: () => void;
    activeTemplateNameMirror: string;
    changeActiveTemplateNameMirror: (value: string) => void;
    suggestedName: string;
    onTemplateDelete: (templateId: number) => void;
    canEdit?: boolean;
    totalTemplates: number;
    onTemplatesPageChange: () => void;
};

const ScheduleTemplatesPanel = ({
    isOpen,
    flipIsOpen,
    templates,
    selectedTemplateId,
    onTemplateSelect,
    isEditing,
    flipIsEditing,
    activeTemplateNameMirror,
    changeActiveTemplateNameMirror,
    suggestedName,
    onTemplateDelete,
    canEdit,
    flipIsCreating,
    isCreating,
    totalTemplates,
    onTemplatesPageChange,
}: TProps): JSX.Element => {
    const [activeKey, setActiveKey] = useState('template-list');

    function getNamePlaceholder() {
        const curTemplate =
            selectedTemplateId != -1 ? templates.find((t) => t.id == selectedTemplateId) : null;
        return curTemplate?.title ?? suggestedName;
    }

    useEffect(() => {
        if (!canEdit) setActiveKey('');
    }, [canEdit]);

    return (
        <Collapse
            bordered={false}
            activeKey={[activeKey]}
            expandIcon={(_props) => {
                return (
                    <Button
                        className="green-btn btn-icon-separated"
                        icon={<FontAwesomeIcon icon={isOpen ? faAngleLeft : faAngleDown} />}
                        onClick={() => {
                            flipIsOpen();
                            setActiveKey(!isOpen ? 'template-list' : '');
                        }}
                        style={{ width: canEdit ? '' : '93%' }}
                    >
                        Шаблоны
                    </Button>
                );
            }}
            expandIconPlacement="end"
            styles={{
                header: {
                    backgroundColor: 'rgb(255, 201, 140)',
                    border: '2px solid rgb(154, 101, 59)',
                    borderRadius: 12,
                    color: 'black',
                    fontSize: 16,
                    fontWeight: 500,
                    alignItems: 'center',
                    cursor: 'default',
                },
                body: {
                    backgroundColor: 'rgb(255, 201, 140)',
                    border: '2px solid rgb(154, 101, 59)',
                    borderTop: 'none',
                    borderBottomLeftRadius: 12,
                    borderBottomRightRadius: 12,
                    padding: 0,
                },
            }}
        >
            <Panel
                className="template-panel"
                collapsible="icon"
                header={
                    canEdit ? (
                        <TextArea
                            autoSize={{ minRows: 1, maxRows: 3 }}
                            className="template-header"
                            onChange={(e) => {
                                changeActiveTemplateNameMirror(e.target.value);
                            }}
                            placeholder={getNamePlaceholder()}
                            rows={2}
                            value={activeTemplateNameMirror}
                        />
                    ) : (
                        <div style={{ height: '36px' }} />
                    )
                }
                key="template-list"
            >
                <InfiniteScroll
                    dataLength={templates.length}
                    next={onTemplatesPageChange}
                    hasMore={templates.length < totalTemplates}
                    loader={<Divider plain>Загрузка...</Divider>}
                    endMessage={<Divider plain>Все шаблоны загружены :^)</Divider>}
                    height={'200px'}
                >
                    <Row align="top" className="template-stack">
                        {templates.length > 0 &&
                            templates.map((t, i) => {
                                return (
                                    <Col
                                        span={24}
                                        className="half-width"
                                        key={'template-' + i}
                                        style={isEditing ? { paddingRight: '10px' } : {}}
                                    >
                                        <Button
                                            className={
                                                t.id == selectedTemplateId
                                                    ? 'set-focus btn-center-text'
                                                    : 'btn-center-text'
                                            }
                                            onClick={(e) => {
                                                if (isEditing) return;
                                                onTemplateSelect(t.id);
                                                e.currentTarget.blur();
                                                // if (isOpen) flipIsOpen();
                                                // setActiveKey('');
                                                if (isCreating) flipIsCreating();
                                            }}
                                            style={isEditing ? { marginTop: '10px' } : {}}
                                        >
                                            {`${i + 1}.${!!t.is_basic ? 'График компании' : ''} ${
                                                t.title
                                            }`}
                                            {isEditing && (
                                                <div
                                                    className="delete-btn"
                                                    onClick={() => onTemplateDelete(t.id)}
                                                    style={{
                                                        backgroundColor:
                                                            t.schedules.length == 0
                                                                ? 'red'
                                                                : '#808080',
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faXmark} />
                                                </div>
                                            )}
                                        </Button>
                                    </Col>
                                );
                            })}
                    </Row>
                </InfiniteScroll>
                {canEdit && (
                    <Row align="middle" className="template-stack">
                        <Col className="half-width">
                            <Button
                                className="btn-icon-separated"
                                danger={isEditing}
                                icon={<FontAwesomeIcon icon={faEdit} />}
                                onClick={() => flipIsEditing()}
                                type="default"
                                style={{ backgroundColor: '#fafa9c' }}
                            >
                                Редактировать список
                            </Button>
                        </Col>
                        <Col className="half-width">
                            <Button
                                className="green-btn btn-icon-separated"
                                danger={isCreating}
                                icon={<FontAwesomeIcon icon={isCreating ? faCancel : faPlus} />}
                                onClick={() => {
                                    flipIsCreating();
                                    onTemplateSelect(selectedTemplateId);
                                }}
                                type="default"
                            >
                                {isCreating ? 'Отмена' : 'Создать новый шаблон'}
                            </Button>
                        </Col>
                    </Row>
                )}
            </Panel>
        </Collapse>
    );
};

export { ScheduleTemplatesPanel };
